// No prototype reference — follows design system.
// Operation creation modal - FOP-S4.
// 4 steps: Details -> Zones -> Teams -> Review.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Zone } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { useVenues } from '../hooks/useVenues';
import { useOperations } from '../hooks/useOperations';
import { useTeams, type CrowdTeam } from '../hooks/useTeams';
import { useCrowdTaxonomy, type CrowdTaxonomyItem } from '../hooks/useCrowdTaxonomy';
import { CreateVenueModal } from './CreateVenueModal';
import { ZoneCreationWizard } from '../screens/zones';
import { get } from '@vigilo/api-client';
import {
  Btn, Badge, KLabel, StatusDot,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, RADIUS, SIZE, SPACE,
  Icons, type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';
import { MOCK_ZONES } from '../data/mock';
import { crowdQuery } from '../apiScope';

// ── Constants ─────────────────────────────────────────────────────────────────

const ZONE_SHORT_TYPE: Record<string, string> = {
  entry_exit_gate:      'GATE',
  standing_area:        'STND',
  seated_section:       'SEAT',
  vip_area:             'VIP',
  circulation_corridor: 'CIRC',
  service_area:         'SVC',
  assembly_point:       'ASSM',
  restricted_zone:      'RSTR',
  perimeter:            'PERI',
  temporary_ops:        'TOPS',
};

const ZONE_TYPE_LABEL: Record<string, string> = {
  entry_exit_gate:      'Entry/Exit Gate',
  standing_area:        'Standing Area',
  seated_section:       'Seated Section',
  vip_area:             'VIP Area',
  circulation_corridor: 'Corridor',
  service_area:         'Service Area',
  assembly_point:       'Assembly Point',
  restricted_zone:      'Restricted Zone',
  perimeter:            'Perimeter',
  temporary_ops:        'Temp Ops',
};

function fmtCap(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function toLocalDatetimeStr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const DEFAULT_OPERATION_DURATION_MINUTES = 8 * 60;

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

function defaultStart(): string {
  return toLocalDatetimeStr(new Date());
}
function defaultEnd(start = defaultStart()): string {
  return toLocalDatetimeStr(addMinutes(new Date(start), DEFAULT_OPERATION_DURATION_MINUTES));
}

function operationDurationMinutes(start: string, end: string): number {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return DEFAULT_OPERATION_DURATION_MINUTES;
  }
  return Math.max(1, Math.round((endMs - startMs) / 60_000));
}

function operationWindowForSubmit(
  start: string,
  end: string,
  activateNow: boolean,
): { scheduledStart: string; scheduledEnd: string } {
  if (!activateNow) {
    return {
      scheduledStart: new Date(start).toISOString(),
      scheduledEnd: new Date(end).toISOString(),
    };
  }

  const actualSubmitStart = new Date();
  const durationMinutes = operationDurationMinutes(start, end);
  return {
    scheduledStart: actualSubmitStart.toISOString(),
    scheduledEnd: addMinutes(actualSubmitStart, durationMinutes).toISOString(),
  };
}

function isOperationStartDue(isoStart: string): boolean {
  const startMs = Date.parse(isoStart);
  return Number.isFinite(startMs) && startMs <= Date.now();
}

function currentOrSelectedStart(value: string): string {
  const current = defaultStart();
  return new Date(value).getTime() < new Date(current).getTime() ? current : value;
}

function fmtSchedule(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const p = (n: number) => String(n).padStart(2, '0');
  const startStr = `${p(s.getHours())}:${p(s.getMinutes())}`;
  const endStr   = `${p(e.getHours())}:${p(e.getMinutes())}`;
  const dateStr  = s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${startStr} – ${endStr} · ${dateStr}`;
}

// ── Step indicator ────────────────────────────────────────────────────────────

type OperationWizardStep = 1 | 2 | 3 | 4;

const STEP_LABELS = ['Details', 'Zones', 'Teams', 'Review'] as const;
const STEP_TITLES = [
  'Operation Details',
  'Select & Configure Zones',
  'Assign Teams',
  'Review & Activate',
] as const;

function StepIndicator({ step, C }: { step: OperationWizardStep; C: ColorPalette }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACE[24] }}>
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const isActive   = n === step;
        const isComplete = n < step;
        const dotColor   = isActive ? C.amber : isComplete ? C.teal : C.muted;
        const labelColor = isActive ? C.text  : isComplete ? C.sub  : C.muted;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEP_LABELS.length - 1 ? 1 : undefined }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACE[3] }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: isActive ? C.amberDim : isComplete ? C.tealDim : C.faint,
                border: `2px solid ${dotColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isComplete
                  ? <Icons.Check size={12} />
                  : <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: dotColor }}>{n}</span>
                }
              </div>
              <span style={{
                fontSize: FONT_SIZE.label, fontWeight: isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.medium,
                color: labelColor, letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 1, background: i + 1 < step ? C.teal : C.border2,
                margin: `0 ${SPACE[8]}px`, marginBottom: SPACE[16],
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Compact toggle ────────────────────────────────────────────────────────────

function CompactToggle({ on, onChange, label, C, testId }: {
  on: boolean; onChange: (v: boolean) => void; label?: string; C: ColorPalette; testId?: string;
}) {
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], cursor: 'pointer' }}
      onClick={() => onChange(!on)}>
      <span style={{
        width: 28, height: 16, borderRadius: 8, flexShrink: 0,
        background: on ? C.amber : C.faint,
        border: `1px solid ${on ? C.amber : C.border3}`,
        position: 'relative', display: 'inline-block', transition: 'all 150ms',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: on ? 13 : 2,
          width: 10, height: 10, borderRadius: '50%', background: '#fff', transition: 'all 150ms',
        }} />
      </span>
      {label && (
        <span style={{ fontSize: FONT_SIZE.xs, color: on ? C.amber : C.muted, fontWeight: on ? FONT_WEIGHT.bold : undefined }}>
          {label}
        </span>
      )}
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────

function inputStyle(C: ColorPalette): React.CSSProperties {
  return {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.md,
    padding: `${SPACE[7]}px ${SPACE[10]}px`, fontSize: FONT_SIZE.body, color: C.text,
    fontFamily: "'Albert Sans', sans-serif", outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };
}

// ── Step 1 — Details ──────────────────────────────────────────────────────────

function Step1Details({ C, name, setName, eventType, setEventType, venueId, setVenueId,
  venueLocked, venues, eventTypes, eventTypesLoading, scheduledStart, setScheduledStart,
  scheduledEnd, setScheduledEnd, activateNow, setActivateNow, onCreateVenueRequest }: {
  C:                 ColorPalette;
  name:              string;
  setName:           (v: string) => void;
  eventType:         string;
  setEventType:      (v: string) => void;
  venueId:           string;
  setVenueId:        (v: string) => void;
  venueLocked:       boolean;
  venues:            { id: string; name: string; city: string; totalCapacity: number }[];
  eventTypes:        CrowdTaxonomyItem[];
  eventTypesLoading: boolean;
  scheduledStart:    string;
  setScheduledStart: (v: string) => void;
  scheduledEnd:      string;
  setScheduledEnd:   (v: string) => void;
  activateNow:       boolean;
  setActivateNow:    (v: boolean) => void;
  onCreateVenueRequest: () => void;
}) {
  const iStyle = inputStyle(C);
  const selectedVenue = venues.find(v => v.id === venueId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[14] }}>
      <div>
        <p style={{ margin: `0 0 ${SPACE[20]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
          Define the venue, operation type, schedule, and activation state for this monitoring window.
        </p>
      </div>

      {/* Operation Name */}
      <div>
        <KLabel C={C}>Operation Name <span style={{ color: C.ember }}>*</span></KLabel>
        <input
          data-testid="operation-name-input"
          style={iStyle}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. UEFA Champions League — Group Stage"
          maxLength={100}
          autoFocus
        />
        {name.length > 0 && name.trim().length < 3 && (
          <div style={{ fontSize: FONT_SIZE.xs, color: C.ember, marginTop: SPACE[3] }}>
            Name must be at least 3 characters
          </div>
        )}
      </div>

      {/* Operation Type */}
      <div>
        <KLabel C={C}>Operation Type <span style={{ color: C.ember }}>*</span></KLabel>
        <select
          data-testid="operation-type-select"
          style={iStyle}
          value={eventType}
          onChange={e => setEventType(e.target.value)}
          disabled={eventTypesLoading || eventTypes.length === 0}
        >
          <option value="">{eventTypesLoading ? 'Loading operation types...' : 'Select operation type...'}</option>
          {eventTypes.map(t => (
            <option key={t.code} value={t.code}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Venue */}
      <div>
        <KLabel C={C}>Venue <span style={{ color: C.ember }}>*</span></KLabel>
        {venueLocked ? (
          <div style={{
            ...iStyle,
            display: 'flex', alignItems: 'center', gap: SPACE[8],
            color: C.sub, background: C.faint, cursor: 'default',
          }}>
            <Icons.Lock size={SIZE.iconSm} />
            <span>{selectedVenue?.name ?? venueId} — {selectedVenue?.city}</span>
          </div>
        ) : (
          <select
            data-testid="operation-venue-select"
            style={iStyle}
            value={venueId}
            onChange={e => {
              if (e.target.value === '__create__') {
                onCreateVenueRequest();
                return;
              }
              setVenueId(e.target.value);
            }}
          >
            <option value="">Select venue…</option>
            {venues.map(v => (
              <option key={v.id} value={v.id}>{v.name} — {v.city}</option>
            ))}
            <option value="__create__">+ Create new venue</option>
          </select>
        )}
      </div>

      <div style={{
        padding: `${SPACE[10]}px ${SPACE[12]}px`,
        background: C.faint, borderRadius: RADIUS.card, border: `1px solid ${C.border}`,
        display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: SPACE[12],
      }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold }}>
            {selectedVenue?.name ?? 'Selected venue'}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>
            Venue capacity {selectedVenue ? fmtCap(selectedVenue.totalCapacity) : '-'} persons
          </div>
        </div>
        <Badge C={C} color={C.teal} bg={C.tealDim} size={9}>Capacity Context</Badge>
      </div>

      {/* Schedule */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[10] }}>
        <div>
          <KLabel C={C}>Scheduled Start <span style={{ color: C.ember }}>*</span></KLabel>
          <input
            data-testid="operation-start-input"
            type="datetime-local"
            style={iStyle}
            value={scheduledStart}
            min={defaultStart()}
            onChange={e => setScheduledStart(currentOrSelectedStart(e.target.value))}
          />
        </div>
        <div>
          <KLabel C={C}>Scheduled End <span style={{ color: C.ember }}>*</span></KLabel>
          <input
            data-testid="operation-end-input"
            type="datetime-local"
            style={iStyle}
            value={scheduledEnd}
            onChange={e => setScheduledEnd(e.target.value)}
          />
        </div>
      </div>
      {scheduledStart && scheduledEnd && new Date(scheduledEnd) <= new Date(scheduledStart) && (
        <div style={{
          fontSize: FONT_SIZE.xs, color: C.ember, background: C.emberDim,
          border: `1px solid ${C.ember}44`, borderRadius: RADIUS.sm,
          padding: `${SPACE[6]}px ${SPACE[8]}px`,
        }}>
          Scheduled end must be after scheduled start
        </div>
      )}

      {/* Activate Now */}
      <div style={{
        padding: `${SPACE[10]}px ${SPACE[12]}px`,
        background: C.faint, borderRadius: RADIUS.card, border: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: SPACE[12],
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold }}>
            Activate immediately on creation
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>
            Operation will go live as soon as it is created.
          </div>
        </div>
        <CompactToggle on={activateNow} onChange={setActivateNow} C={C} testId="operation-activate-toggle" />
      </div>
    </div>
  );
}

// ── Step 2 — Zone assignment ──────────────────────────────────────────────────

function Step1VenueType({ C, eventType, setEventType, venueId, setVenueId,
  venueLocked, venues, eventTypes, eventTypesLoading, onCreateVenueRequest }: {
  C:                 ColorPalette;
  eventType:         string;
  setEventType:      (v: string) => void;
  venueId:           string;
  setVenueId:        (v: string) => void;
  venueLocked:       boolean;
  venues:            { id: string; name: string; city: string; totalCapacity: number }[];
  eventTypes:        CrowdTaxonomyItem[];
  eventTypesLoading: boolean;
  onCreateVenueRequest: () => void;
}) {
  const iStyle = inputStyle(C);
  const selectedVenue = venues.find(v => v.id === venueId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[14] }}>
      <p style={{ margin: `0 0 ${SPACE[20]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
        Select the controlled venue and governed operation type.
      </p>

      <div>
        <KLabel C={C}>Venue <span style={{ color: C.ember }}>*</span></KLabel>
        {venueLocked ? (
          <div style={{
            ...iStyle,
            display: 'flex', alignItems: 'center', gap: SPACE[8],
            color: C.sub, background: C.faint, cursor: 'default',
          }}>
            <Icons.Lock size={SIZE.iconSm} />
            <span>{selectedVenue?.name ?? venueId} - {selectedVenue?.city}</span>
          </div>
        ) : (
          <select
            data-testid="operation-venue-select"
            style={iStyle}
            value={venueId}
            onChange={e => {
              if (e.target.value === '__create__') {
                onCreateVenueRequest();
                return;
              }
              setVenueId(e.target.value);
            }}
          >
            <option value="">Select venue...</option>
            {venues.map(v => (
              <option key={v.id} value={v.id}>{v.name} - {v.city}</option>
            ))}
            <option value="__create__">+ Create new venue</option>
          </select>
        )}
      </div>

      <div>
        <KLabel C={C}>Operation Type <span style={{ color: C.ember }}>*</span></KLabel>
        <select
          data-testid="operation-type-select"
          style={iStyle}
          value={eventType}
          onChange={e => setEventType(e.target.value)}
          disabled={eventTypesLoading || eventTypes.length === 0}
        >
          <option value="">{eventTypesLoading ? 'Loading operation types...' : 'Select operation type...'}</option>
          {eventTypes.map(t => (
            <option key={t.code} value={t.code}>{t.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Step2Details({ C, name, setName, venueId, venues, scheduledStart, setScheduledStart,
  scheduledEnd, setScheduledEnd, activateNow, setActivateNow }: {
  C:                 ColorPalette;
  name:              string;
  setName:           (v: string) => void;
  venueId:           string;
  venues:            { id: string; name: string; city: string; totalCapacity: number }[];
  scheduledStart:    string;
  setScheduledStart: (v: string) => void;
  scheduledEnd:      string;
  setScheduledEnd:   (v: string) => void;
  activateNow:       boolean;
  setActivateNow:    (v: boolean) => void;
}) {
  const iStyle = inputStyle(C);
  const selectedVenue = venues.find(v => v.id === venueId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[14] }}>
      <p style={{ margin: `0 0 ${SPACE[20]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
        Set the operational name, monitoring window, and venue capacity context.
      </p>

      <div>
        <KLabel C={C}>Operation Name <span style={{ color: C.ember }}>*</span></KLabel>
        <input
          data-testid="operation-name-input"
          style={iStyle}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. UEFA Champions League - Group Stage"
          maxLength={100}
          autoFocus
        />
        {name.length > 0 && name.trim().length < 3 && (
          <div style={{ fontSize: FONT_SIZE.xs, color: C.ember, marginTop: SPACE[3] }}>
            Name must be at least 3 characters
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[10] }}>
        <div>
          <KLabel C={C}>Scheduled Start <span style={{ color: C.ember }}>*</span></KLabel>
          <input
            data-testid="operation-start-input"
            type="datetime-local"
            style={iStyle}
            value={scheduledStart}
            onChange={e => setScheduledStart(e.target.value)}
          />
        </div>
        <div>
          <KLabel C={C}>Scheduled End <span style={{ color: C.ember }}>*</span></KLabel>
          <input
            data-testid="operation-end-input"
            type="datetime-local"
            style={iStyle}
            value={scheduledEnd}
            onChange={e => setScheduledEnd(e.target.value)}
          />
        </div>
      </div>
      {scheduledStart && scheduledEnd && new Date(scheduledEnd) <= new Date(scheduledStart) && (
        <div style={{
          fontSize: FONT_SIZE.xs, color: C.ember, background: C.emberDim,
          border: `1px solid ${C.ember}44`, borderRadius: RADIUS.sm,
          padding: `${SPACE[6]}px ${SPACE[8]}px`,
        }}>
          Scheduled end must be after scheduled start
        </div>
      )}

      <div style={{
        padding: `${SPACE[10]}px ${SPACE[12]}px`,
        background: C.faint, borderRadius: RADIUS.card, border: `1px solid ${C.border}`,
        display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: SPACE[12],
      }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold }}>
            {selectedVenue?.name ?? 'Selected venue'}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>
            Venue capacity {selectedVenue ? fmtCap(selectedVenue.totalCapacity) : '-'} persons
          </div>
        </div>
        <Badge C={C} color={C.teal} bg={C.tealDim} size={9}>Capacity Context</Badge>
      </div>

      <div style={{
        padding: `${SPACE[10]}px ${SPACE[12]}px`,
        background: C.faint, borderRadius: RADIUS.card, border: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: SPACE[12],
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold }}>
            Activate immediately on creation
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>
            Operation will go live as soon as it is created.
          </div>
        </div>
        <CompactToggle on={activateNow} onChange={setActivateNow} C={C} testId="operation-activate-toggle" />
      </div>
    </div>
  );
}

function Step2Zones({ C, zones, zonesLoading, selectedZoneIds, setSelectedZoneIds,
  tempZoneIds, onToggleTemp, onCreateZoneRequest }: {
  C:                ColorPalette;
  zones:            Zone[];
  zonesLoading:     boolean;
  selectedZoneIds:  string[];
  setSelectedZoneIds: (ids: string[]) => void;
  tempZoneIds:      Set<string>;
  onToggleTemp:     (id: string) => void;
  onCreateZoneRequest: () => void;
}) {
  const allSelected = zones.length > 0 && selectedZoneIds.length === zones.length;

  function toggleZone(id: string) {
    setSelectedZoneIds(
      selectedZoneIds.includes(id)
        ? selectedZoneIds.filter(z => z !== id)
        : [...selectedZoneIds, id]
    );
  }

  return (
    <div>
      <p style={{ margin: `0 0 ${SPACE[16]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
        Select the zones to monitor during this operation
      </p>

      {zonesLoading ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: C.muted, fontSize: FONT_SIZE.body }}>
          Loading zones…
        </div>
      ) : zones.length === 0 ? (
        <div style={{
          padding: '30px 0', textAlign: 'center', color: C.muted,
          fontSize: FONT_SIZE.body,
        }}>
          <div style={{ marginBottom: SPACE[8] }}>No zones configured at this venue.</div>
          <div style={{ margin: `0 auto ${SPACE[12]}px`, maxWidth: 420, color: C.sub, lineHeight: 1.5 }}>
            You can create a venue-linked zone now. After it is saved, assign operation team coverage in the next step,
            or continue with venue scope only.
          </div>
          <button
            onClick={onCreateZoneRequest}
            style={{
              background: 'none', border: 'none', color: C.amber, cursor: 'pointer',
              fontSize: FONT_SIZE.body, fontFamily: "'Albert Sans', sans-serif",
              textDecoration: 'underline',
            }}
          >
            + Create Zone
          </button>
        </div>
      ) : (
        <>
          {/* Select all / deselect */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: SPACE[8] }}>
            <button
              onClick={() => setSelectedZoneIds(allSelected ? [] : zones.map(z => z.id))}
              style={{
                background: 'none', border: 'none', color: C.amber, cursor: 'pointer',
                fontSize: FONT_SIZE.xs, fontFamily: "'Albert Sans', sans-serif",
              }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Zone list */}
          <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE[2] }}>
            {zones.map(zone => {
              const checked   = selectedZoneIds.includes(zone.id);
              const isTemp    = tempZoneIds.has(zone.id);
              const stColor   = zone.status === 'normal' ? C.green : zone.status === 'warning' ? C.amber : C.ember;
              return (
                <div
                  key={zone.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: SPACE[10],
                    padding: `${SPACE[8]}px ${SPACE[10]}px`,
                    borderRadius: RADIUS.card,
                    background: checked ? C.amberDim : C.faint,
                    border: `1px solid ${checked ? C.amber + '40' : C.border}`,
                    cursor: 'pointer', transition: 'all 120ms',
                  }}
                  onClick={() => toggleZone(zone.id)}
                >
                  {/* Checkbox */}
                  <div style={{
                    width: 16, height: 16, borderRadius: RADIUS.sm, flexShrink: 0,
                    background: checked ? C.amber : 'transparent',
                    border: `2px solid ${checked ? C.amber : C.border3}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && <Icons.Check size={10} />}
                  </div>

                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                      {zone.name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], marginTop: SPACE[1] }}>
                      <Badge C={C} color={C.sub} bg="transparent" size={9}>
                        {ZONE_TYPE_LABEL[zone.type] ?? zone.type}
                      </Badge>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: FONT_SIZE.xs, color: C.muted }}>
                        <Icons.Users size={9} />{fmtCap(zone.capacity)}
                      </span>
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <StatusDot color={stColor} size={5} />
                    <span style={{ fontSize: FONT_SIZE.xs, color: stColor, textTransform: 'capitalize' }}>
                      {zone.status}
                    </span>
                  </div>

                  {/* Temp toggle */}
                  <div
                    title="Temporary — archived when operation closes"
                    onClick={e => { e.stopPropagation(); onToggleTemp(zone.id); }}
                    style={{ flexShrink: 0 }}
                  >
                    <CompactToggle on={isTemp} onChange={() => onToggleTemp(zone.id)} label={isTemp ? 'Temp' : undefined} C={C} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[10] }}>
            {selectedZoneIds.length} zone{selectedZoneIds.length !== 1 ? 's' : ''} selected ·{' '}
            {selectedZoneIds.filter(id => !tempZoneIds.has(id)).length} permanent ·{' '}
            {selectedZoneIds.filter(id => tempZoneIds.has(id)).length} temporary
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 3 — Confirm ──────────────────────────────────────────────────────────

type ZoneTeamOverrideMap = Record<string, string | null>;

function Step4Teams({ C, teams, teamsLoading, teamsError, zones, selectedZoneIds,
  defaultTeamId, setDefaultTeamId, zoneTeamOverrides, setZoneTeamOverride }: {
  C: ColorPalette;
  teams: CrowdTeam[];
  teamsLoading: boolean;
  teamsError: string | null;
  zones: Zone[];
  selectedZoneIds: string[];
  defaultTeamId: string;
  setDefaultTeamId: (v: string) => void;
  zoneTeamOverrides: ZoneTeamOverrideMap;
  setZoneTeamOverride: (zoneId: string, teamId: string | null | undefined) => void;
}) {
  const iStyle = inputStyle(C);
  const selectedZones = zones.filter(z => selectedZoneIds.includes(z.id));
  const teamById = new Map(teams.map(team => [team.id, team]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[14] }}>
      <p style={{ margin: `0 0 ${SPACE[4]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
        Set the default operations team, then override only zones that require separate coverage.
      </p>

      <div>
        <KLabel C={C}>Default team for this operation</KLabel>
        <select
          data-testid="operation-default-team-select"
          style={iStyle}
          value={defaultTeamId}
          onChange={e => setDefaultTeamId(e.target.value)}
          disabled={teamsLoading || teams.length === 0}
        >
          <option value="">{teamsLoading ? 'Loading teams...' : 'No default team'}</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
        {teamsError && (
          <div style={{ fontSize: FONT_SIZE.xs, color: C.ember, marginTop: SPACE[4] }}>{teamsError}</div>
        )}
      </div>

      <div style={{
        border: `1px solid ${C.border}`,
        borderRadius: RADIUS.card,
        overflow: 'hidden',
        background: C.faint,
      }}>
        {selectedZones.map((zone, index) => {
          const hasOverride = Object.prototype.hasOwnProperty.call(zoneTeamOverrides, zone.id);
          const overrideTeamId = zoneTeamOverrides[zone.id] ?? null;
          const effectiveTeamId = hasOverride ? overrideTeamId : defaultTeamId || null;
          const effectiveTeam = effectiveTeamId ? teamById.get(effectiveTeamId) : null;
          const selectValue = hasOverride
            ? (overrideTeamId ?? '__unassigned__')
            : '__same__';

          return (
            <div
              key={zone.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(210px, 260px) auto',
                alignItems: 'center',
                gap: SPACE[10],
                padding: `${SPACE[10]}px ${SPACE[12]}px`,
                borderTop: index === 0 ? 'none' : `1px solid ${C.border}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                  {zone.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], marginTop: SPACE[2] }}>
                  <Badge C={C} color={C.sub} bg="transparent" size={9}>
                    {ZONE_TYPE_LABEL[zone.type] ?? zone.type}
                  </Badge>
                  <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>{fmtCap(zone.capacity)} capacity</span>
                </div>
              </div>

              <select
                data-testid={`operation-zone-team-select-${zone.id}`}
                style={iStyle}
                value={selectValue}
                disabled={teamsLoading || teams.length === 0}
                onChange={e => {
                  if (e.target.value === '__same__') {
                    setZoneTeamOverride(zone.id, undefined);
                    return;
                  }
                  if (e.target.value === '__unassigned__') {
                    setZoneTeamOverride(zone.id, null);
                    return;
                  }
                  setZoneTeamOverride(zone.id, e.target.value);
                }}
              >
                <option value="__same__">Same as operation</option>
                <option value="__unassigned__">No team assigned</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>

              <Badge C={C} color={hasOverride ? C.amber : C.teal} bg={hasOverride ? C.amberDim : C.tealDim} size={9}>
                {hasOverride ? (effectiveTeam?.name ?? 'Override') : 'Same as operation'}
              </Badge>
            </div>
          );
        })}
      </div>

      {teams.length === 0 && !teamsLoading && (
        <div style={{
          fontSize: FONT_SIZE.xs, color: C.muted, background: C.faint,
          border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
          padding: `${SPACE[8]}px ${SPACE[10]}px`,
        }}>
          No active teams are configured in Crowd Admin.
        </div>
      )}
      {selectedZones.length === 0 && (
        <div style={{
          fontSize: FONT_SIZE.xs, color: C.muted, background: C.faint,
          border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
          padding: `${SPACE[8]}px ${SPACE[10]}px`,
        }}>
          No zones are assigned yet. Team coverage can be added after venue zones are configured.
        </div>
      )}
    </div>
  );
}

function Step3Confirm({ C, name, eventType, eventTypeLabel, venueId, venues, scheduledStart,
  scheduledEnd, activateNow, zones, selectedZoneIds, tempZoneIds, teams, defaultTeamId,
  zoneTeamOverrides, submitError }: {
  C:              ColorPalette;
  name:           string;
  eventType:      string;
  eventTypeLabel: (type: string) => string;
  venueId:        string;
  venues:         { id: string; name: string; city: string; totalCapacity: number }[];
  scheduledStart: string;
  scheduledEnd:   string;
  activateNow:    boolean;
  zones:          Zone[];
  selectedZoneIds: string[];
  tempZoneIds:    Set<string>;
  teams:          CrowdTeam[];
  defaultTeamId:  string;
  zoneTeamOverrides: ZoneTeamOverrideMap;
  submitError:    string | null;
}) {
  const venueName = venues.find(v => v.id === venueId)?.name ?? venueId;
  const eventLabel = eventTypeLabel(eventType);
  const selectedZones = zones.filter(z => selectedZoneIds.includes(z.id));
  const teamById = new Map(teams.map(team => [team.id, team]));
  const defaultTeamName = defaultTeamId ? teamById.get(defaultTeamId)?.name ?? 'Selected team' : 'No default team';

  return (
    <div>
      <p style={{ margin: `0 0 ${SPACE[16]}px`, fontSize: FONT_SIZE.body, color: C.muted }}>
        Confirm venue scope, zone coverage, team accountability, and activation state.
      </p>

      <div style={{
        background: C.panelHi, borderRadius: RADIUS.panel, border: `1px solid ${C.border2}`,
        padding: `${SPACE[16]}px`, display: 'flex', flexDirection: 'column', gap: SPACE[12],
      }}>
        {/* Name */}
        <div style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
          {name}
        </div>

        {/* Event type + venue */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
          <Badge C={C} color={C.amber} bg={C.amberDim} size={9}>{eventLabel}</Badge>
          <span style={{ fontSize: FONT_SIZE.body, color: C.sub }}>{venueName}</span>
        </div>

        {/* Schedule */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], fontSize: FONT_SIZE.body, color: C.sub }}>
          <Icons.Clock size={SIZE.iconSm} />
          <span>{fmtSchedule(scheduledStart, scheduledEnd)}</span>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, margin: `${SPACE[2]}px 0` }} />

        {/* Zones */}
        <div>
          <KLabel C={C} mb={8}>{selectedZones.length} Zones Assigned</KLabel>
          {selectedZones.length === 0 ? (
            <div style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
              Venue scope only. Zones can be assigned after they are configured for this venue.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE[4] }}>
              {selectedZones.map(z => (
                <div key={z.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: SPACE[4],
                  background: C.surface, border: `1px solid ${C.border2}`,
                  borderRadius: RADIUS.pill, padding: `${SPACE[3]}px ${SPACE[8]}px`,
                  fontSize: FONT_SIZE.xs,
                }}>
                  <span style={{ color: C.text, fontWeight: FONT_WEIGHT.semibold }}>{z.name}</span>
                  <span style={{ color: C.muted }}>{ZONE_SHORT_TYPE[z.type] ?? z.type.toUpperCase().slice(0, 4)}</span>
                  {tempZoneIds.has(z.id) && (
                    <span style={{ color: C.amber, fontSize: 9, fontWeight: FONT_WEIGHT.bold }}>T</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <KLabel C={C} mb={8}>Team Coverage</KLabel>
          <div style={{ fontSize: FONT_SIZE.body, color: C.sub, marginBottom: SPACE[8] }}>
            Default team: <span style={{ color: C.text, fontWeight: FONT_WEIGHT.semibold }}>{defaultTeamName}</span>
          </div>
          {selectedZones.length === 0 ? (
            <div style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
              Per-zone team coverage will be available after zones are assigned.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[4] }}>
              {selectedZones.map(zone => {
                const hasOverride = Object.prototype.hasOwnProperty.call(zoneTeamOverrides, zone.id);
                const teamId = hasOverride ? zoneTeamOverrides[zone.id] : defaultTeamId || null;
                const teamName = teamId ? teamById.get(teamId)?.name ?? 'Selected team' : 'Unassigned';
                return (
                  <div key={zone.id} style={{
                    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                    alignItems: 'center', gap: SPACE[8],
                    fontSize: FONT_SIZE.xs, color: C.sub,
                  }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{zone.name}</span>
                    <span style={{ color: C.text, fontWeight: FONT_WEIGHT.semibold }}>{teamName}</span>
                    <Badge C={C} color={hasOverride ? C.amber : C.teal} bg={hasOverride ? C.amberDim : C.tealDim} size={9}>
                      {hasOverride ? 'Override' : 'Same as operation'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Activate now info */}
      <div style={{
        marginTop: SPACE[12],
        padding: `${SPACE[10]}px ${SPACE[12]}px`,
        background: activateNow ? C.amberDim : C.faint,
        border: `1px solid ${activateNow ? C.amber + '44' : C.border}`,
        borderRadius: RADIUS.card,
        display: 'flex', gap: SPACE[8],
      }}>
        <Icons.Info size={SIZE.iconSm} />
        <div>
          {activateNow ? (
            <>
              <div style={{ fontSize: FONT_SIZE.base, color: C.amber, fontWeight: FONT_WEIGHT.semibold }}>
                This operation will be activated immediately.
              </div>
              <div style={{ fontSize: FONT_SIZE.xs, color: C.sub, marginTop: SPACE[2] }}>
                {selectedZones.length === 0
                  ? 'Venue scope will be active. Assign zones after they are configured for monitoring.'
                  : 'Crowd monitoring will begin for all assigned zones.'}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: FONT_SIZE.base, color: C.sub, fontWeight: FONT_WEIGHT.semibold }}>
                Operation will be created in Scheduled status.
              </div>
              <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>
                Activate it from the sidebar when ready to begin monitoring.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Submit error */}
      {submitError && (
        <div style={{
          marginTop: SPACE[10],
          padding: `${SPACE[8]}px ${SPACE[12]}px`,
          background: C.emberDim, border: `1px solid ${C.ember}`,
          borderRadius: RADIUS.card, fontSize: FONT_SIZE.base, color: C.ember,
        }}>
          {submitError}
        </div>
      )}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export function OperationCreateModal() {
  const {
    theme, currentUser,
    operationModalVenueId, closeOperationModal,
    addActiveOperation, setSelectedOperation, refreshOperations,
  } = useApp();
  const { isMock } = useMockMode();
  const C = getCrowdC(theme);
  const { venues, createVenue } = useVenues();
  const { createOperation, activateOperation } = useOperations();
  const { teams, loading: teamsLoading, error: teamsError } = useTeams();
  const {
    taxonomy,
    loading: taxonomyLoading,
    operationEventTypeLabel,
  } = useCrowdTaxonomy();

  const canCreate = currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';
  if (!canCreate) return null;

  const venueLocked = !!operationModalVenueId;

  // Step 1 state
  const [step,           setStep]           = useState<OperationWizardStep>(1);
  const [name,           setName]           = useState('');
  const [eventType,      setEventType]      = useState<string>('');
  const [venueId,        setVenueId]        = useState(operationModalVenueId ?? '');
  const [scheduledStart, setScheduledStart] = useState(() => defaultStart());
  const [scheduledEnd,   setScheduledEnd]   = useState(() => defaultEnd(scheduledStart));
  const [activateNow,    setActivateNow]    = useState(false);

  // Step 2 state
  const [zones,            setZones]            = useState<Zone[]>([]);
  const [zonesLoading,     setZonesLoading]      = useState(false);
  const [selectedZoneIds,  setSelectedZoneIds]   = useState<string[]>([]);
  const [tempZoneIds,      setTempZoneIds]        = useState<Set<string>>(new Set());
  const [defaultTeamId,    setDefaultTeamId]      = useState('');
  const [zoneTeamOverrides, setZoneTeamOverrides] = useState<ZoneTeamOverrideMap>({});

  // Step 3 state
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success,     setSuccess]     = useState(false);
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [showCreateZone,  setShowCreateZone]  = useState(false);

  const operationEventTypes = taxonomy?.operationEventTypes ?? [];

  useEffect(() => {
    if (!eventType && operationEventTypes.length > 0) {
      setEventType(operationEventTypes[0].code);
    }
  }, [eventType, operationEventTypes]);

  const readVenueZones = useCallback(async (id: string): Promise<Zone[]> => {
    if (isMock) {
      return MOCK_ZONES.filter(z => z.venueId === id);
    }
    const body = await get<Zone[] | { data?: Zone[] }>(`/venues/${id}/zones?${crowdQuery({ archiveStatus: 'active' })}`);
    return Array.isArray(body) ? body : (body.data ?? []);
  }, [isMock]);

  async function reloadVenueZones(id: string, selectZone?: Zone): Promise<void> {
    setZonesLoading(true);
    try {
      const loadedZones = await readVenueZones(id);
      const nextZones = selectZone && selectZone.venueId === id && !loadedZones.some(zone => zone.id === selectZone.id)
        ? [selectZone, ...loadedZones]
        : loadedZones;
      setZones(nextZones);
      setTempZoneIds(new Set(nextZones.filter(zone => zone.temporary).map(zone => zone.id)));
      if (selectZone?.id) {
        setSelectedZoneIds(prev => [...new Set([...prev, selectZone.id])]);
      }
    } catch {
      setZones([]);
      setTempZoneIds(new Set());
    } finally {
      setZonesLoading(false);
    }
  }

  // Load zones whenever venueId changes
  useEffect(() => {
    if (!venueId) {
      setZones([]);
      setSelectedZoneIds([]);
      setTempZoneIds(new Set());
      setZoneTeamOverrides({});
      return;
    }
    void reloadVenueZones(venueId);
    setSelectedZoneIds([]);
    setZoneTeamOverrides({});
  }, [venueId, readVenueZones]);

  useEffect(() => {
    setZoneTeamOverrides(prev => {
      const selected = new Set(selectedZoneIds);
      const next = Object.fromEntries(
        Object.entries(prev).filter(([zoneId]) => selected.has(zoneId)),
      ) as ZoneTeamOverrideMap;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [selectedZoneIds]);

  // Validation
  const step1Valid = !!eventType && !!venueId;

  const step2Valid =
    name.trim().length >= 3 &&
    !!scheduledStart && !!scheduledEnd &&
    new Date(scheduledEnd) > new Date(scheduledStart);

  const step3Valid = !zonesLoading;

  const activeTeams = useMemo(
    () => teams.filter(team => team.status === 'active' && team.archivedAt === null),
    [teams],
  );
  const teamById = useMemo(() => new Map(activeTeams.map(team => [team.id, team])), [activeTeams]);

  function setZoneTeamOverride(zoneId: string, teamId: string | null | undefined) {
    setZoneTeamOverrides(prev => {
      const next = { ...prev };
      if (teamId === undefined) {
        delete next[zoneId];
      } else {
        next[zoneId] = teamId;
      }
      return next;
    });
  }

  function buildZoneTeamAssignments() {
    return selectedZoneIds.map(zoneId => {
      const hasOverride = Object.prototype.hasOwnProperty.call(zoneTeamOverrides, zoneId);
      return {
        zoneId,
        teamId: hasOverride ? zoneTeamOverrides[zoneId] : defaultTeamId || null,
        inheritedFromDefault: !hasOverride,
      };
    });
  }

  function buildAssignedTeamNames(): string[] {
    const names = new Set<string>();
    if (defaultTeamId) {
      const defaultTeamName = teamById.get(defaultTeamId)?.name;
      if (defaultTeamName) names.add(defaultTeamName);
    }
    for (const assignment of buildZoneTeamAssignments()) {
      if (!assignment.teamId) continue;
      const name = teamById.get(assignment.teamId)?.name;
      if (name) names.add(name);
    }
    return [...names];
  }

  function toggleTemp(id: string) {
    setTempZoneIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleCreateVenue(dto: Parameters<typeof createVenue>[0]) {
    const venue = await createVenue(dto);
    setVenueId(venue.id);
    setShowCreateVenue(false);
  }

  async function handleCreateZone(zone?: Zone) {
    setShowCreateZone(false);
    if (!venueId) return;
    await reloadVenueZones(venueId, zone);
    if (zone?.id) {
      setStep(3);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const operationWindow = operationWindowForSubmit(scheduledStart, scheduledEnd, activateNow);
      const op = await createOperation({
        name: name.trim(),
        venueId,
        eventType,
        scheduledStart: operationWindow.scheduledStart,
        scheduledEnd:   operationWindow.scheduledEnd,
        assignedZoneIds: selectedZoneIds,
        temporaryZoneIds: selectedZoneIds.filter(id => tempZoneIds.has(id)),
        assignedTeams: buildAssignedTeamNames(),
        defaultTeamId: defaultTeamId || null,
        zoneTeamAssignments: buildZoneTeamAssignments(),
      });

      let finalOp = op;
      const shouldActivateImmediately = activateNow || isOperationStartDue(operationWindow.scheduledStart);
      if (shouldActivateImmediately) {
        try {
          finalOp = await activateOperation(op.id);
        } catch {
          // Activation failed — op still created
          setSubmitError('Operation created but activation failed. Activate from the sidebar.');
          await refreshOperations();
          setTimeout(closeOperationModal, 3000);
          return;
        }
      }

      // Update sidebar
      if (finalOp.status === 'active' || finalOp.status === 'paused') {
        addActiveOperation(finalOp);
        setSelectedOperation(finalOp);
      } else {
        await refreshOperations();
      }

      setSuccess(true);
      setTimeout(closeOperationModal, 1500);
    } catch (err) {
      setSubmitError((err as Error).message ?? 'Failed to create operation — check Core API');
    } finally {
      setSubmitting(false);
    }
  }

  const activeVenues = venues.filter(v => v.archivedAt === null).map(v => ({
    id: v.id, name: v.name, city: v.city, totalCapacity: v.totalCapacity,
  }));

  const stepTitle = STEP_TITLES[step - 1];

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        width: SIZE.opModal, maxWidth: '92vw',
        background: C.panel, borderRadius: RADIUS.panel,
        border: `1px solid ${C.border2}`,
        maxHeight: '90vh', overflowY: 'auto',
        fontFamily: "'Albert Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: `${SPACE[20]}px ${SPACE[24]}px ${SPACE[16]}px`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div>
            <div style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
              {stepTitle}
            </div>
          </div>
          <button
            onClick={closeOperationModal}
            style={{
              width: 28, height: 28, borderRadius: RADIUS.pill, border: 'none',
              background: C.surface, color: C.muted, cursor: 'pointer',
              fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Albert Sans', sans-serif",
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: `${SPACE[20]}px ${SPACE[24]}px` }}>
          <StepIndicator step={step} C={C} />

          {success ? (
            <div style={{
              textAlign: 'center', padding: `${SPACE[32]}px 0`,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: C.greenDim, border: `2px solid ${C.green}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: `0 auto ${SPACE[14]}px`,
              }}>
                <Icons.Check size={20} />
              </div>
              <div style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[4] }}>
                {activateNow ? 'Operation Activated' : 'Operation Created'}
              </div>
              <div style={{ fontSize: FONT_SIZE.base, color: C.muted }}>
                {activateNow ? 'Crowd monitoring has begun for all assigned zones.' : 'Operation scheduled — activate from the sidebar when ready.'}
              </div>
            </div>
          ) : (
            <>
              {step === 1 && (
                <Step1Details
                  C={C}
                  name={name} setName={setName}
                  eventType={eventType} setEventType={setEventType}
                  venueId={venueId} setVenueId={v => { setVenueId(v); }}
                  venueLocked={venueLocked}
                  venues={activeVenues}
                  eventTypes={operationEventTypes}
                  eventTypesLoading={taxonomyLoading}
                  scheduledStart={scheduledStart} setScheduledStart={setScheduledStart}
                  scheduledEnd={scheduledEnd}     setScheduledEnd={setScheduledEnd}
                  activateNow={activateNow} setActivateNow={setActivateNow}
                  onCreateVenueRequest={() => setShowCreateVenue(true)}
                />
              )}
              {step === 2 && (
                <Step2Zones
                  C={C}
                  zones={zones} zonesLoading={zonesLoading}
                  selectedZoneIds={selectedZoneIds} setSelectedZoneIds={setSelectedZoneIds}
                  tempZoneIds={tempZoneIds} onToggleTemp={toggleTemp}
                  onCreateZoneRequest={() => setShowCreateZone(true)}
                />
              )}
              {step === 3 && (
                <Step4Teams
                  C={C}
                  teams={activeTeams}
                  teamsLoading={teamsLoading}
                  teamsError={teamsError}
                  zones={zones}
                  selectedZoneIds={selectedZoneIds}
                  defaultTeamId={defaultTeamId}
                  setDefaultTeamId={setDefaultTeamId}
                  zoneTeamOverrides={zoneTeamOverrides}
                  setZoneTeamOverride={setZoneTeamOverride}
                />
              )}
              {step === 4 && (
                <Step3Confirm
                  C={C}
                  name={name} eventType={eventType}
                  eventTypeLabel={operationEventTypeLabel}
                  venueId={venueId} venues={activeVenues}
                  scheduledStart={scheduledStart} scheduledEnd={scheduledEnd}
                  activateNow={activateNow}
                  zones={zones} selectedZoneIds={selectedZoneIds} tempZoneIds={tempZoneIds}
                  teams={activeTeams}
                  defaultTeamId={defaultTeamId}
                  zoneTeamOverrides={zoneTeamOverrides}
                  submitError={submitError}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div style={{
            padding: `${SPACE[14]}px ${SPACE[24]}px ${SPACE[20]}px`,
            borderTop: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              {step > 1 && (
                <Btn C={C} variant="ghost" icon={<Icons.ChevL size={SIZE.iconSm} />}
                  onClick={() => setStep(s => (s - 1) as OperationWizardStep)}>
                  Back
                </Btn>
              )}
              {step === 1 && (
                <Btn C={C} variant="ghost" onClick={closeOperationModal}>Cancel</Btn>
              )}
            </div>

            <div>
              {step < 4 && (
                <Btn
                  data-testid="operation-wizard-next"
                  C={C} variant="primary"
                  icon={<Icons.ArrowR size={SIZE.iconSm} />}
                  disabled={step === 1 ? !(step1Valid && step2Valid) : step === 2 ? !step3Valid : false}
                  onClick={() => setStep(s => (s + 1) as OperationWizardStep)}
                >
                  Next
                </Btn>
              )}
              {step === 4 && (
                <Btn
                  data-testid="operation-wizard-submit"
                  C={C} variant="primary"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting
                    ? (activateNow ? 'Activating…' : 'Creating…')
                    : (activateNow ? 'Create & Activate' : 'Create Operation')}
                </Btn>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    {showCreateVenue && (
      <CreateVenueModal
        C={C}
        onClose={() => setShowCreateVenue(false)}
        onCreate={handleCreateVenue}
      />
    )}
    {showCreateZone && venueId && (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100,
      }}>
        <div style={{
          width: 'min(1180px, 96vw)', height: '90vh',
          background: C.bg, border: `1px solid ${C.border2}`,
          borderRadius: RADIUS.panel, overflow: 'hidden', display: 'flex',
        }}>
          <ZoneCreationWizard
            C={C}
            createForVenueId={venueId}
            createForOperationId={null}
            assignmentContextNote="Team coverage is assigned in the Operation team step after this venue-linked zone is saved."
            onClose={() => setShowCreateZone(false)}
            onSuccess={handleCreateZone}
          />
        </div>
      </div>
    )}
    </>
  );
}
